import { createReducer, on } from '@ngrx/store';
import {
  TrustedDestination,
  TrustedDestinationListMeta,
} from '../models/trusted-destination.model';
import * as TrustedDestinationsActions from './trusted-destinations.actions';
import { ApiTableFilter } from '../../../shared/models/table-filter.model';

export interface TrustedDestinationsQueryState {
  search?: string;
  filters?: ApiTableFilter[];
  filterLink?: string;
  sort?: string;
  sortDirection?: string;
}

export interface TrustedDestinationsState {
  items: TrustedDestination[];
  meta: TrustedDestinationListMeta | null;
  selectedItem: TrustedDestination | null;
  isLoading: boolean;
  isLoadingItem: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  isExporting: boolean;
  error: string | null;
  lastQuery: TrustedDestinationsQueryState;
}

const initialState: TrustedDestinationsState = {
  items: [],
  meta: null,
  selectedItem: null,
  isLoading: false,
  isLoadingItem: false,
  isSubmitting: false,
  isDeleting: false,
  isExporting: false,
  error: null,
  lastQuery: {},
};

export const trustedDestinationsReducer = createReducer(
  initialState,

  // Load list
  on(TrustedDestinationsActions.loadTrustedDestinations, (state, action) => ({
    ...state,
    isLoading: true,
    error: null,
    lastQuery: {
      search: action.search,
      filters: action.filters,
      filterLink: action.filterLink,
      sort: action.sort,
      sortDirection: action.sortDirection,
    },
  })),
  on(TrustedDestinationsActions.loadTrustedDestinationsSuccess, (state, { items, meta }) => ({
    ...state,
    items,
    meta,
    isLoading: false,
  })),
  on(TrustedDestinationsActions.loadTrustedDestinationsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Load single item
  on(TrustedDestinationsActions.loadTrustedDestination, (state) => ({
    ...state,
    isLoadingItem: true,
    error: null,
  })),
  on(TrustedDestinationsActions.loadTrustedDestinationSuccess, (state, { item }) => ({
    ...state,
    selectedItem: item,
    isLoadingItem: false,
  })),
  on(TrustedDestinationsActions.loadTrustedDestinationFailure, (state, { error }) => ({
    ...state,
    isLoadingItem: false,
    error,
  })),

  // Create
  on(TrustedDestinationsActions.createTrustedDestination, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
  })),
  on(TrustedDestinationsActions.createTrustedDestinationSuccess, (state) => ({
    ...state,
    isSubmitting: false,
  })),
  on(TrustedDestinationsActions.createTrustedDestinationFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
  })),

  // Update
  on(TrustedDestinationsActions.updateTrustedDestination, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
  })),
  on(TrustedDestinationsActions.updateTrustedDestinationSuccess, (state) => ({
    ...state,
    isSubmitting: false,
  })),
  on(TrustedDestinationsActions.updateTrustedDestinationFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
  })),

  // Delete
  on(TrustedDestinationsActions.deleteTrustedDestination, (state) => ({
    ...state,
    isDeleting: true,
    error: null,
  })),
  on(TrustedDestinationsActions.deleteTrustedDestinationSuccess, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(TrustedDestinationsActions.deleteTrustedDestinationFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    error,
  })),

  // Export
  on(TrustedDestinationsActions.exportTrustedDestinations, (state) => ({
    ...state,
    isExporting: true,
    error: null,
  })),
  on(TrustedDestinationsActions.exportTrustedDestinationsSuccess, (state) => ({
    ...state,
    isExporting: false,
  })),
  on(TrustedDestinationsActions.exportTrustedDestinationsFailure, (state, { error }) => ({
    ...state,
    isExporting: false,
    error,
  })),

  // Selection
  on(TrustedDestinationsActions.selectTrustedDestination, (state, { item }) => ({
    ...state,
    selectedItem: item,
  })),
  on(TrustedDestinationsActions.clearSelection, (state) => ({
    ...state,
    selectedItem: null,
  })),

  // Reset
  on(TrustedDestinationsActions.resetTrustedDestinationsState, () => initialState)
);
